import type { Task } from "~skillBuilder/utils/types";

import { image, tip, title } from "../../contentBlocks";

export const PAdaptability = {
  name: "Adaptability and Problem Solving",
  uniqueName: "p-adaptability",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.adaptability/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Adaptability and Problem Solving", durationTimeText: "10 min" })}
<p>Schedules shift, technology glitches happen, and unexpected challenges pop up—it's all part of the job. Staying adaptable and keeping a positive attitude helps maintain the focus on what matters most: student learning.</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202463/skillbuilderAssets/optimized/problem_solving.jpg", alt: "Illustration of women holding a paper and problem solving." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.adaptability/flexibility",
          label: "Flexibility",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
School partners value substitutes who approach changes in schedules, lesson plans, or classroom dynamics with a positive, can-do attitude. Being flexible helps keep the classroom running smoothly and supports student learning.

<ul style="margin-left: 16px;">
<li>📝 <strong>Be ready for changes</strong> – As appropriate, adjust to schedule shifts, updated lesson plans, or different classroom dynamics just like students adapt to new directions.</li>
<li>🌟 <strong>Bring a positive attitude</strong> – A can-do mindset keeps the day running smoothly and creates a productive learning environment.</li>
<li>🎯 <strong>Stay adaptable and professional</strong> – Handling unexpected challenges calmly shows reliability and supports student success.</li>
<li>💡 <strong>Think on your feet</strong> – Quick, thoughtful adjustments help lessons stay on track and students stay engaged.</li>
</ul>

By staying flexible, you demonstrate reliability, foster a positive classroom environment, and help students succeed—making your impact as a substitute teacher meaningful and lasting.
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.adaptability/management",
          label: "Effectively managing classroom behavior",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Schools depend on substitutes to actively engage with students and help maintain a positive, focused classroom environment. By being attentive and involved, substitutes support learning and encourage students to stay on task and meet expectations.

<ul style="margin-left: 16px;">
<li>📝 <strong>Stay engaged with students</strong> – Participate in lessons alongside students to build connection and stay aware of classroom dynamics.</li>
<li>🌟 <strong>Notice and address issues early</strong> – Spot off-task or disruptive behavior before it escalates.</li>
<li>🎯 <strong>Reinforce expectations</strong> – Your presence and attentiveness help students understand and follow classroom rules.</li>
<li>💡 <strong>Support student focus</strong> – Active involvement encourages students to stay on track and contributes to a positive learning environment.</li>
</ul>
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.adaptability/tech",
          label: "Navigating technology",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Many classrooms are equipped with Chromebooks or other devices for students, and schools often use a Learning Management System (LMS) to share assignments, resources, and schedules.

Take a few minutes to familiarize yourself with classroom technology such as interactive whiteboards (Promethean, SMARTBoard, ELMO), projectors, tablets, or desktop computers.

${tip({ text: "If technology isn't cooperating, stay flexible—ask a student to share their screen then walk the classroom to provide assistance for students who need direction. This quick pivot keeps the lesson on track without unnecessary delays." })}
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.adaptability/support",
          label: "When to seek support",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Know how and when to ask for help. Professionalism isn't about having all the answers—it's about using resources and collaborating with staff to support student success.

<ul style="margin-left: 16px;">
<li>📅 <strong>Reach out when the schedule or expectations are unclear</strong> – Clarifying early prevents confusion and helps the day run smoothly.</li>
<li>📖 <strong>Ask for guidance on school policies or procedures</strong> – Ensuring you follow school standards shows respect and reliability.</li>
<li>👩‍🏫 <strong>Seek support if student behavior becomes unmanageable</strong> – Involving school staff helps maintain a safe, productive environment.</li>
</ul>

Staying proactive and communicative shows leadership and builds trust with school staff.
            `,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
